import { useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";
import { trackLeadCreated, trackMetaLead } from "@/lib/analytics";

import { Button } from "@/components/ui/button";
import { buildTrackingFields, recordLeadInDb } from "@/lib/lead";
import { markFormFocus, parsePhoneBR } from "@/lib/tracking";
import { checkAntispam, markAntispamSubmitted, antispamMessagePt } from "@/lib/antispam";
import { useLocale } from "@/i18n/LocaleProvider";

const PARTNER_WEBHOOK_URL =
  "https://kentrosistemas.atenderbem.com/webhookcapture/capture/f220cf1eb0c84d28bf4c806b59d1f031";

const PARTNER_TYPES = [
  "Agência / Consultoria",
  "Revenda / Reseller",
  "Integrador / Tecnologia",
  "Afiliado / Indicador",
  "Outro",
] as const;

const AUDIENCES = [
  "Até 10 contatos",
  "10 a 50 contatos",
  "50 a 200 contatos",
  "Mais de 200 contatos",
] as const;

type PartnerFormValues = {
  name: string;
  email: string;
  phone: string;
  company: string;
  website?: string;
  partnerType: (typeof PARTNER_TYPES)[number];
  audience: (typeof AUDIENCES)[number];
  segment?: string;
  message?: string;
};

export function PartnerForm() {
  const locale = useLocale();
  const [done, setDone] = useState(false);
  const formOpenedAtRef = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(2, "Informe seu nome").max(120),
        email: z.string().trim().email("E-mail inválido").max(200),
        phone: z
          .string()
          .trim()
          .min(8, "Telefone inválido")
          .max(25)
          .regex(/^[\d\s()+\-.]+$/, "Use apenas números")
          .refine(
            (v) => {
              const digits = v.replace(/\D/g, "");
              const national = digits.startsWith("55") && (digits.length === 12 || digits.length === 13)
                ? digits.slice(2)
                : digits;
              return national.length === 10 || national.length === 11;
            },
            { message: "Telefone deve ter DDD + número (10 ou 11 dígitos)" },
          ),
        company: z.string().trim().min(2, "Informe sua empresa").max(200),
        website: z.string().trim().max(200).optional().or(z.literal("")),
        partnerType: z.enum(PARTNER_TYPES, { errorMap: () => ({ message: "Selecione" }) }),
        audience: z.enum(AUDIENCES, { errorMap: () => ({ message: "Selecione" }) }),
        segment: z.string().trim().max(120).optional().or(z.literal("")),
        message: z.string().trim().max(2000).optional().or(z.literal("")),
      }),
    [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PartnerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      website: "",
      partnerType: "Agência / Consultoria",
      audience: "Até 10 contatos",
      segment: "",
      message: "",
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const scope = "parceria";
    const spam = await checkAntispam({
      scope,
      honeypot: honeypotRef.current?.value ?? "",
      formOpenedAt: formOpenedAtRef.current,
      contentKey: `${data.name}|${data.email}|${data.phone}|${data.company}`,
    });
    if (!spam.ok) {
      toast.error(antispamMessagePt(spam) ?? "Não foi possível enviar agora.");
      return;
    }
    try {
      const formId = "kentro-parceria";
      // Tudo coletado de forma defensiva — nunca quebra mesmo sem storage/UTMs.
      let trackingFields: Record<string, string> = {};
      try {
        trackingFields = buildTrackingFields(formId, "parceria") || {};
      } catch (e) {
        console.warn("[parceria] buildTrackingFields falhou", e);
      }

      const phone = parsePhoneBR(data.phone);
      // Sanitiza: força strings, remove undefined/null e trims.
      const s = (v: unknown) => (typeof v === "string" ? v.trim() : v == null ? "" : String(v));

      const fields: Record<string, string> = {
        // Identificação do lead
        name: s(data.name),
        email: s(data.email),
        phone: phone.national,
        phone_e164: phone.e164,
        phone_ddd: phone.ddd,
        phone_national: phone.national,
        phone_valid: String(phone.valid),
        company: s(data.company),
        // Específicos do programa de parcerias
        partner_type: s(data.partnerType),
        partner_audience: s(data.audience),
        website: s(data.website),
        segment: s(data.segment),
        message: s(data.message),
        // Metadados padrão
        locale: s(locale),
      };
      // Mescla origem resolvida, UTMs, click IDs e sinais de rastreio.
      for (const [k, v] of Object.entries(trackingFields)) {
        fields[k] = s(v);
      }

      const payload = {
        fields,
        page_url: typeof window !== "undefined" ? window.location.href : "",
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
        language: typeof navigator !== "undefined" ? navigator.language : "",
        timestamp: new Date().toISOString(),
        form_id: formId,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      };

      const res = await fetch(PARTNER_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Webhook respondeu ${res.status}`);

      recordLeadInDb({ ...fields, page_url: payload.page_url });

      // Conversão real na Meta (Pixel + Conversions API, deduplicado por event_id).
      const nameParts = s(data.name).split(/\s+/).filter(Boolean);
      trackMetaLead(
        {
          content_name: "Programa de Parcerias Kentro",
          form_id: formId,
          form_source: "parceria",
          partner_type: fields.partner_type,
          partner_audience: fields.partner_audience,
          source: fields.source || "parceria",
        },
        {
          email: s(data.email),
          phone: phone.e164 || phone.national,
          first_name: nameParts[0],
          last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined,
          country: "br",
        },
      );
      trackLeadCreated("parceria");
      markAntispamSubmitted(scope, spam.hash);
      setDone(true);
      reset();
      formOpenedAtRef.current = Date.now();
      toast.success("Recebemos sua candidatura!", {
        description: "Nosso time de parcerias entrará em contato em breve.",
      });
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar", {
        description: "Tente novamente em instantes.",
      });
    }
  });

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-4 size-12 text-primary-glow" />
        <h3 className="text-xl font-semibold">Candidatura enviada</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Em até 2 dias úteis nosso time de parcerias entra em contato com você.
        </p>
        <Button variant="ghost" className="mt-4" onClick={() => setDone(false)}>
          Enviar outra
        </Button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full rounded-lg bg-secondary/60 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary-glow focus:ring-2 focus:ring-primary/30";

  return (
    <form
      onSubmit={onSubmit}
      onFocus={() => markFormFocus("kentro-parceria")}
      className="space-y-4"
    >
      {/* honeypot anti-spam (diferente do campo "website" real do parceiro) */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="hp-parceria">Do not fill</label>
        <input ref={honeypotRef} id="hp-parceria" name="hp_url" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nome *</label>
          <input className={inputCls} placeholder="Seu nome completo" {...register("name")} />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            WhatsApp *
          </label>
          <input
            className={inputCls}
            placeholder="(11) 91234-5678"
            inputMode="tel"
            {...register("phone")}
          />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            E-mail corporativo *
          </label>
          <input
            className={inputCls}
            placeholder="voce@empresa.com.br"
            type="email"
            {...register("email")}
          />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Empresa *
          </label>
          <input className={inputCls} placeholder="Nome da empresa" {...register("company")} />
          {errors.company && (
            <p className="mt-1 text-xs text-destructive">{errors.company.message}</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Site / LinkedIn
          </label>
          <input
            className={inputCls}
            placeholder="https://"
            {...register("website")}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Segmento de atuação
          </label>
          <input
            className={inputCls}
            placeholder="Ex: Saúde, Varejo, Educação"
            {...register("segment")}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Tipo de parceria *
          </label>
          <select aria-label="Tipo de parceria" className={inputCls} {...register("partnerType")}>
            {PARTNER_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Base de clientes / audiência *
          </label>
          <select aria-label="Base de clientes / audiência" className={inputCls} {...register("audience")}>
            {AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
          Conte sobre seu negócio e o tipo de parceria que você busca
        </label>
        <textarea
          className={inputCls}
          rows={4}
          placeholder="Atendo PMEs no segmento X, gostaria de revender / indicar..."
          {...register("message")}
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full gradient-primary text-white shadow-glow hover:opacity-95"
        size="lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin" /> Enviando...
          </>
        ) : (
          <>
            <Handshake /> Quero ser parceiro Kentro
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Ao enviar você concorda com nossa{" "}
        <a href="/privacidade" className="underline hover:text-foreground">
          Política de Privacidade
        </a>
        .
      </p>
    </form>
  );
}
